import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getServiceDetails, getNearbyVendors, updateService, getServiceSubtypes, getCostEstimate } from '../services/vendorSearch.service';
import { paymentService } from '../services/payment.service';
import VendorCard from '../components/VendorSearch/VendorCard';
import VendorDetailsModal from '../components/VendorSearch/VendorDetailsModal';
import FuelStationModal from '../components/VendorSearch/FuelStationModal';
import Map from '../components/VendorSearch/Map';

const VendorSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get serviceId and caseId from URL params or location state (fallback)
  const serviceId = searchParams.get('serviceId') || location.state?.serviceId;
  const caseId = searchParams.get('case_id') || location.state?.case_id;

  const [serviceDetails, setServiceDetails] = useState(null);
  const [nearbyVendors, setNearbyVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedVendorData, setSelectedVendorData] = useState(null);
  const [routeDistances, setRouteDistances] = useState({});
  // Fuel-related state
  const [showFuelStationModal, setShowFuelStationModal] = useState(false);
  const [tempFuelLocation, setTempFuelLocation] = useState(null);
  const [savingFuelLocation, setSavingFuelLocation] = useState(false);

  // Service subtype filter state
  const [serviceSubtypes, setServiceSubtypes] = useState([]);
  const [selectedServiceSubtypeId, setSelectedServiceSubtypeId] = useState(null);
  const [loadingServiceSubtypes, setLoadingServiceSubtypes] = useState(false);

  // Search radius state
  const [searchRadius, setSearchRadius] = useState(100); // Default 100km

  // Loading state for vendor refresh
  const [refreshingVendors, setRefreshingVendors] = useState(false);



  // Fetch service subtypes when service details are loaded
  const fetchServiceSubtypes = useCallback(async (serviceTypeId) => {
    if (!serviceTypeId) return;

    setLoadingServiceSubtypes(true);
    try {
      const response = await getServiceSubtypes(serviceTypeId);
      setServiceSubtypes(response || []);
    } catch (error) {
      console.error('Error fetching service subtypes:', error);
      setServiceSubtypes([]);
    } finally {
      setLoadingServiceSubtypes(false);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!serviceId) {
        setError('No service ID provided');
        setLoading(false);
        return;
      }

      try {
        const serviceData = await getServiceDetails(serviceId);
        setServiceDetails(serviceData);

        // Set default service subtype to current service's subtype
        const currentServiceSubtypeId = serviceData?.service?.scheme_service_subtype?.id;
        setSelectedServiceSubtypeId(currentServiceSubtypeId);

        // Fetch service subtypes for the current service type
        const serviceTypeId = serviceData?.service?.scheme_service_type?.id;
        if (serviceTypeId) {
          await fetchServiceSubtypes(serviceTypeId);
        }

        // Extract location from service details for nearby vendors search
        const serviceLocation = serviceData?.service?.pickup_location;

        if (serviceLocation?.latitude && serviceLocation?.longitude) {
          const vendorsData = await getNearbyVendors(
            serviceLocation.latitude,
            serviceLocation.longitude,
            searchRadius, // Use search radius from state
            10, // limit to 10 vendors
            currentServiceSubtypeId // Pass current service subtype as default filter
          );
          setNearbyVendors(vendorsData.vendor_locations || []);
        } else {
          console.error('Service location missing coordinates:', serviceLocation);
          setError('Service location coordinates not available');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // searchRadius intentionally omitted so radius changes use explicit refresh handlers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, fetchServiceSubtypes]);

  const handleVendorSelect = useCallback((vendorId) => {
    console.log('Vendor selected from map:', vendorId);
    setSelectedVendorId(vendorId);

    // Scroll to the selected vendor card if it's not visible
    setTimeout(() => {
      const selectedCard = document.querySelector(`[data-vendor-id="${vendorId}"]`);
      if (selectedCard) {
        selectedCard.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);
  }, []);

  const handleRouteCalculated = useCallback((routeData) => {
    setRouteDistances(prev => ({
      ...prev,
      [routeData.vendorId]: {
        outboundDistance: routeData.outboundDistance,
        returnDistance: routeData.returnDistance,
        outboundDuration: routeData.outboundDuration,
        totalDistance: routeData.totalDistance,
        isTowingService: routeData.isTowingService,
        hasFuelLocation: routeData.hasFuelLocation,
        // For towing services, include the service distance (pickup → drop)
        ...(routeData.isTowingService && {
          serviceDistance: routeData.serviceDistance,
          serviceDuration: routeData.serviceDuration
        }),
        // For fuel location, include fuel stop details
        ...(routeData.hasFuelLocation && {
          fuelToServiceDistance: routeData.fuelToServiceDistance,
          fuelToServiceDuration: routeData.fuelToServiceDuration,
          fuelToPickupDistance: routeData.fuelToPickupDistance,
          fuelToPickupDuration: routeData.fuelToPickupDuration
        })
      }
    }));
  }, []);

  const handleShowDetails = useCallback((vendorData) => {
    setSelectedVendorData(vendorData);
    setShowDetailsModal(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedVendorData(null);
  }, []);

  const handleSelectVendor = useCallback(async (vendorData) => {
    if (!serviceId) return;

    setAssigning(true);
    try {
      // Determine the appropriate status based on current service status.
      // Self-service schemes must enter estimate/payment (estimate_sent), not jump to assigned.
      const currentStatus = serviceDetails?.service?.status;
      const isPaidCustomer = serviceDetails?.customer?.is_paid_customer;
      const isSelfService =
        Boolean(serviceDetails?.client_scheme?.is_self_service) ||
        String(serviceDetails?.client_scheme?.scheme_type || '').toLowerCase() === 'self_service' ||
        String(serviceDetails?.client?.client_code || '').toLowerCase() === 'self_service';

      let newStatus;
      if (currentStatus === 'scheduled') {
        newStatus = 'vendor_scheduled';
      } else if (isSelfService) {
        // Matches backend status_after_vendor_accept for self-service.
        newStatus = 'estimate_sent';
      } else if (isPaidCustomer) {
        // Vendor is selected but customer must pay before service becomes assigned
        newStatus = 'awaiting_customer_payment';
      } else {
        newStatus = 'assigned';
      }

      const existingMeta = serviceDetails?.service?.meta_data || {};
      const updateData = {
        status: newStatus,
        vendor_location_id: vendorData.vendorLocationId,
        estimated_time_minutes: vendorData.estimatedTimeMinutes,
        distance_km: vendorData.distanceKm,
        notes: `Vendor assigned through web portal. Vendor: ${selectedVendorData?.location?.vendor?.vendor_name}, Manager: ${selectedVendorData?.location?.manager_user?.name}`
      };

      // Mark agent pre-assign so payment success goes to `assigned` and does not
      // re-broadcast SERVICE_ALERT / DriverAssign to the vendor manager.
      if (isSelfService || isPaidCustomer) {
        updateData.meta_data = {
          ...existingMeta,
          payment_pending_vendor_assigned: true,
          ...(vendorData.driverId ? { pending_driver_id: vendorData.driverId } : {}),
        };
      }

      console.log('DEBUG: serviceDetails:', serviceDetails);
      console.log('DEBUG: isPaidCustomer:', isPaidCustomer, 'isSelfService:', isSelfService);

      if (isPaidCustomer || isSelfService) {
        const customerBillAmount =
          vendorData.estimatedClientCost != null
            ? vendorData.estimatedClientCost
            : vendorData.estimatedCost;

        if (customerBillAmount != null) {
          updateData.estimated_cost_to_customer = customerBillAmount;
        } else {
          try {
            const costResponse = await getCostEstimate(
              vendorData.vendorLocationId,
              vendorData.distanceKm,
              serviceDetails.service.scheme_service_type.id,
              selectedServiceSubtypeId || serviceDetails.service.scheme_service_subtype?.id,
              true,
              {
                clientSchemeId: serviceDetails?.service?.client_scheme?.id || serviceDetails?.client_scheme?.id,
                clientServiceRatesId: vendorData.clientServiceRatesId || undefined,
              },
            );

            updateData.estimated_cost_to_customer =
              costResponse.estimated_client_cost ??
              costResponse.estimated_vendor_cost ??
              costResponse.estimated_cost;
          } catch (err) {
            console.error('Error fetching paid customer cost:', err);
          }
        }

        if (vendorData.estimatedCost != null) {
          updateData.estimated_cost = vendorData.estimatedCost;
        }

        if (vendorData.clientServiceRatesId) {
          updateData.client_service_rates_id = vendorData.clientServiceRatesId;
        }
      } else {
        updateData.estimated_cost = vendorData.estimatedCost;
      }

      // Add vendor service rate ID if provided
      if (vendorData.vendorServiceRatesId) {
        updateData.vendor_service_rates_id = vendorData.vendorServiceRatesId;
        updateData.notes += `, Service Rate ID: ${vendorData.vendorServiceRatesId}`;
      }

      // Add fuel location if temp fuel location is selected
      if (tempFuelLocation) {
        updateData.fuel_location = tempFuelLocation;
        updateData.notes += `, Fuel Station: ${tempFuelLocation.landmark}`;
      }

      // Persist selected driver for display; paid-customer status stays awaiting_customer_payment until paid
      if (vendorData.driverId) {
        updateData.driver_id = vendorData.driverId;
        updateData.notes += `, Driver: ${vendorData.driverId}`;
      }

      // Check if the dynamic drop location is different from the original drop location
      let dropLocationUpdated = false;

      // Check if the dynamic drop location is different from the original
      if (isTowingService && dynamicDropLocation &&
        JSON.stringify(dynamicDropLocation) !== JSON.stringify(originalDropLocation)) {
        // Update the drop location with the dynamic drop location
        updateData.drop_location = dynamicDropLocation;
        dropLocationUpdated = true;
      }

      await updateService(serviceId, updateData);

      if (isPaidCustomer && Number(updateData.estimated_cost_to_customer) > 0) {
        try {
          await paymentService.sendPaymentLink(serviceId, {
            mobile_number: serviceDetails?.customer?.mobile_number,
            send_whatsapp: true,
          });
        } catch (linkErr) {
          console.error('Auto-send payment link failed:', linkErr);
        }
      }

      // Show notification if drop location was updated
      if (dropLocationUpdated) {
        // Create a temporary notification element
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 ease-in-out';
        notification.innerHTML = `
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
            </svg>
            <span>Updated the drop location to vendor location</span>
          </div>
        `;
        document.body.appendChild(notification);

        // Remove notification after 3 seconds
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 3000);
      }

      // Close modal and navigate directly to AddService page
      setShowDetailsModal(false);

      // Navigate back to AddService page with correct parameters
      const caseIdToUse = caseId || serviceDetails?.case?.id;
      navigate(`/add-service?case_id=${caseIdToUse}&service_id=${serviceId}`);
    } catch (error) {
      console.error('Error assigning vendor:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.detail ||
        (typeof error?.message === 'string' ? error.message : null);
      setError(
        detail
          ? `Failed to assign vendor: ${detail}`
          : 'Failed to assign vendor. Please try again.'
      );
    } finally {
      setAssigning(false);
    }
    // Additional closure values intentionally stable for this assign action
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, selectedVendorData, serviceDetails, navigate]);

  const handleCancel = useCallback(() => {
    // Navigate back to AddService page with correct parameters
    const caseIdToUse = caseId || serviceDetails?.case?.id;
    navigate(`/add-service?case_id=${caseIdToUse}&service_id=${serviceId}`);
  }, [navigate, caseId, serviceDetails?.case?.id, serviceId]);

  // Handle service subtype filter change
  const handleServiceSubtypeChange = useCallback(async (subtypeId) => {
    setSelectedServiceSubtypeId(subtypeId);
    setRefreshingVendors(true);

    // Add a safety timeout to ensure refreshing state is reset
    const safetyTimeout = setTimeout(() => {
      setRefreshingVendors(false);
    }, 10000); // 10 seconds timeout

    // Re-fetch vendors with new filter
    const serviceLocation = serviceDetails?.service?.pickup_location;
    if (serviceLocation?.latitude && serviceLocation?.longitude) {
      try {
        const vendorsData = await getNearbyVendors(
          serviceLocation.latitude,
          serviceLocation.longitude,
          searchRadius, // Use current search radius
          10, // limit to 10 vendors
          subtypeId // Pass selected service subtype
        );
        setNearbyVendors(vendorsData.vendor_locations || []);
      } catch (err) {
        console.error('Error fetching vendors with subtype filter:', err);
        setError('Failed to fetch vendors with selected filter');
      } finally {
        clearTimeout(safetyTimeout);
        setRefreshingVendors(false);
      }
    } else {
      clearTimeout(safetyTimeout);
      setRefreshingVendors(false);
    }
  }, [serviceDetails, searchRadius]);

  // Handle search radius change
  const handleSearchRadiusChange = useCallback(async (newRadius) => {
    setSearchRadius(newRadius);
    setRefreshingVendors(true);

    // Add a safety timeout to ensure refreshing state is reset
    const safetyTimeout = setTimeout(() => {
      setRefreshingVendors(false);
    }, 10000); // 10 seconds timeout

    // Re-fetch vendors with new radius
    const serviceLocation = serviceDetails?.service?.pickup_location;
    if (serviceLocation?.latitude && serviceLocation?.longitude) {
      try {
        const vendorsData = await getNearbyVendors(
          serviceLocation.latitude,
          serviceLocation.longitude,
          newRadius, // Use new search radius
          10, // limit to 10 vendors
          selectedServiceSubtypeId // Pass current service subtype
        );
        setNearbyVendors(vendorsData.vendor_locations || []);
      } catch (err) {
        console.error('Error fetching vendors with new radius:', err);
        setError('Failed to fetch vendors with new search radius');
      } finally {
        clearTimeout(safetyTimeout);
        setRefreshingVendors(false);
      }
    } else {
      clearTimeout(safetyTimeout);
      setRefreshingVendors(false);
    }
  }, [serviceDetails, selectedServiceSubtypeId]);

  // Handle fuel station selection from modal
  const handleFuelStationSelected = useCallback(async (fuelLocationData) => {
    setSavingFuelLocation(true);
    try {
      // Update the service with the new fuel location
      const updateData = {
        fuel_location: fuelLocationData
      };

      await updateService(serviceId, updateData);

      // Set the temp fuel location for display
      setTempFuelLocation(fuelLocationData);

      // Close the modal
      setShowFuelStationModal(false);

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 ease-in-out';
      notification.innerHTML = `
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
          </svg>
          <span>Fuel station updated successfully</span>
        </div>
      `;
      document.body.appendChild(notification);

      // Remove notification after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);

    } catch (error) {
      console.error('Error updating fuel location:', error);

      // Show error notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 ease-in-out';
      notification.innerHTML = `
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
          </svg>
          <span>Failed to update fuel station. Please try again.</span>
        </div>
      `;
      document.body.appendChild(notification);

      // Remove notification after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);
    } finally {
      setSavingFuelLocation(false);
    }
  }, [serviceId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  // Extract service location from service details
  const serviceLocation = serviceDetails?.service?.pickup_location;
  const originalDropLocation = serviceDetails?.service?.drop_location;
  const fuelLocation = serviceDetails?.service?.fuel_location; // Keep this to show existing fuel locations

  // Check if this is a towing service (has different drop location)
  const isTowingService = originalDropLocation && (
    originalDropLocation.latitude !== serviceLocation?.latitude ||
    originalDropLocation.longitude !== serviceLocation?.longitude ||
    originalDropLocation.address !== serviceLocation?.address
  );

  // Use temp fuel location if available, otherwise use original
  const effectiveFuelLocation = tempFuelLocation ? {
    latitude: tempFuelLocation.latitude,
    longitude: tempFuelLocation.longitude,
    address: tempFuelLocation.address,
    landmark: tempFuelLocation.landmark,
    city: tempFuelLocation.city,
    state: tempFuelLocation.state,
    pincode: tempFuelLocation.pincode
  } : fuelLocation;

  // Check if fuel location is available (either original or temp)
  const hasFuelLocation = effectiveFuelLocation && effectiveFuelLocation.latitude && effectiveFuelLocation.longitude;

  // Calculate dynamic drop location based on selected vendor
  const getDynamicDropLocation = () => {
    if (!isTowingService) return null;

    if (!selectedVendorId) {
      // No vendor selected, use original drop location
      return originalDropLocation;
    }

    // Find selected vendor
    const selectedVendorData = nearbyVendors.find(v => v.location.id === selectedVendorId);
    if (!selectedVendorData) return originalDropLocation;

    // Check if selected vendor is a dealer
    if (selectedVendorData.location.dealer_id) {
      // Vendor is a dealer, use vendor location as drop location
      const vendorLocationAddress = selectedVendorData.location.location_address;
      if (vendorLocationAddress) {
        return {
          address: vendorLocationAddress.address,
          landmark: vendorLocationAddress.landmark || '',
          city: vendorLocationAddress.city,
          state: vendorLocationAddress.state,
          pincode: vendorLocationAddress.pincode,
          latitude: vendorLocationAddress.latitude,
          longitude: vendorLocationAddress.longitude
        };
      }
    }

    // Vendor is not a dealer or no address data, use original drop location
    return originalDropLocation;
  };

  const dynamicDropLocation = getDynamicDropLocation();

  return (
    <div className="max-w-7xl mx-auto">
      {/* Service Location Section */}
      <div className="bg-white rounded-lg bxshadowhd p-6 mb-7 mrgall">
        <h2 className="text-xl font-bold mb-4">
          Find Vendor for Service #{serviceDetails?.service?.service_number}
          {isTowingService && <span className="text-blue-600 text-sm ml-2">(Towing Service)</span>}
        </h2>

        {/* Service subtype filter and fuel station button */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Service Subtype
              </label>
              <div className="relative">
                <select
                  value={selectedServiceSubtypeId || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleServiceSubtypeChange(value === '' ? null : value);
                  }}
                  className="appearance-none px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-full"
                  disabled={loadingServiceSubtypes}
                >
                  <option value="">All Service Subtypes</option>
                  {loadingServiceSubtypes ? (
                    <option disabled>Loading...</option>
                  ) : (
                    serviceSubtypes.map(subtype => (
                      <option key={subtype.id} value={subtype.id}>
                        {subtype.subtype_name}
                      </option>
                    ))
                  )}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Radius (km)
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={searchRadius}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 100) {
                      handleSearchRadiusChange(value);
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-20"
                  placeholder="50"
                />
                <span className="text-sm text-gray-500">km</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowFuelStationModal(true)}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            {hasFuelLocation ? 'Change Fuel Station' : 'Add Fuel Station'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pickup Location */}
          <div className="space-y-2">
            <h3 className="font-medium text-green-700">Pickup Location</h3>
            <div className="text-gray-600 space-y-1">
              <p className="font-medium">Address:</p>
              <p>{serviceLocation?.address}</p>
              <p>
                {serviceLocation?.city}, {serviceLocation?.state}
              </p>
              <p className="text-sm text-gray-500">
                Coordinates: {serviceLocation?.latitude}, {serviceLocation?.longitude}
              </p>
            </div>
          </div>

          {/* Fuel Location - Show effective fuel location */}
          {hasFuelLocation && (
            <div className="space-y-2">
              <h3 className="font-medium text-orange-700">
                Fuel Station {tempFuelLocation ? '(Selected)' : '(Existing)'}
              </h3>
              <div className="text-gray-600 space-y-1">
                <p className="font-medium">Address:</p>
                <p>{effectiveFuelLocation?.address}</p>
                <p>
                  {effectiveFuelLocation?.city}, {effectiveFuelLocation?.state}
                </p>
                <p className="text-sm text-gray-500">
                  Coordinates: {effectiveFuelLocation?.latitude}, {effectiveFuelLocation?.longitude}
                </p>
                {tempFuelLocation && (
                  <p className="text-sm text-orange-600 font-medium">
                    ✓ New fuel station selected
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Drop Location - Only for towing services */}
          {isTowingService && (
            <div className="space-y-2">
              <h3 className="font-medium text-blue-700">
                Drop Location
                {selectedVendorId && dynamicDropLocation !== originalDropLocation && (
                  <span className="text-green-600 text-sm ml-2">(Updated to vendor location)</span>
                )}
              </h3>
              <div className="text-gray-600 space-y-1">
                <p className="font-medium">Address:</p>
                <p>{dynamicDropLocation?.address}</p>
                <p>
                  {dynamicDropLocation?.city}, {dynamicDropLocation?.state}
                </p>
                <p className="text-sm text-gray-500">
                  Coordinates: {dynamicDropLocation?.latitude}, {dynamicDropLocation?.longitude}
                </p>
                {selectedVendorId && dynamicDropLocation !== originalDropLocation && (
                  <p className="text-sm text-green-600 font-medium">
                    ✓ Drop location updated to selected dealer's location
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>



      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mrgall">
        {/* Map Section */}
        <div className="bg-white shadow p-4 rounded-lg bxshadow mt-4">
          <h3 className="text-lg font-semibold mb-4">Map View</h3>
          <Map
            key={`map-${serviceId}-${hasFuelLocation ? 'with-fuel' : 'no-fuel'}-${tempFuelLocation ? 'temp' : 'original'}`}
            serviceLocation={serviceLocation}
            fuelLocation={hasFuelLocation ? effectiveFuelLocation : null} // Pass effective fuel location to map
            dropLocation={isTowingService ? dynamicDropLocation : null}
            vendors={nearbyVendors}
            selectedVendorId={selectedVendorId}
            onVendorSelect={handleVendorSelect}
            onRouteCalculated={handleRouteCalculated}
            isTowingService={isTowingService}
            hasFuelLocation={hasFuelLocation}
          />
        </div>

        {/* Available Vendors Section */}
        <div className="bg-white rounded-lg bxshadowhd p-4 mt-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            Available Vendors
            {refreshingVendors && (
              <span className="text-blue-600 text-sm ml-2 flex items-center">
                <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Refreshing...
              </span>
            )}
          </h3>
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {nearbyVendors.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No vendors available in the area
              </div>
            ) : (
              nearbyVendors.map((vendorData) => (
                <VendorCard
                  key={vendorData.location.id}
                  vendorLocationData={vendorData}
                  isSelected={selectedVendorId === vendorData.location.id}
                  onSelect={handleVendorSelect}
                  onShowDetails={handleShowDetails}
                  routeDistances={routeDistances}
                  isTowingService={isTowingService}
                  hasFuelLocation={hasFuelLocation}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-center space-x-4 mt-6">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>

      {/* Vendor Details Modal */}
      <VendorDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseDetails}
        vendorLocationData={selectedVendorData}
        serviceDetails={serviceDetails}
        routeDistances={routeDistances}
        onSelectVendor={handleSelectVendor}
        isAssigning={assigning}
        hasFuelLocation={hasFuelLocation}
      />

      {/* Fuel Station Selection Modal */}
      <FuelStationModal
        isOpen={showFuelStationModal}
        onClose={() => setShowFuelStationModal(false)}
        serviceLocation={serviceLocation}
        onFuelStationSelected={handleFuelStationSelected}
        isSaving={savingFuelLocation}
      />
    </div>
  );
};

export default VendorSearch;
